import { useCallback } from 'react';
import toast from 'react-hot-toast';
import type { BuilderPromoOutcome } from 'shared/types/cart';
import { Log } from 'helpers/errorLogger';
import { clearPendingPromoCode, getPendingPromoCode, holdPendingPromoCode } from 'helpers/promoCode';
import useCart from 'frontastic/hooks/useCart';
import useGTM from 'frontastic/hooks/useGTM';

export type PromoCodeOutcome =
  /** Redeemed against the cart right now. */
  | 'applied'
  /** No cart to attach to yet — held in the cookie until the shopper adds an item. */
  | 'deferred'
  /** A different code is already applied and only one is allowed per order. */
  | 'blocked'
  /** This same code is already on the cart; nothing to do. */
  | 'alreadyApplied'
  /** The server rejected it for a reason that may not hold later (cart predicate not met
   *  yet, not started); the code stays held so a later cart change retries it. */
  | 'notYetEligible'
  | 'failed';

/**
 * Codes whose state means "not right now" rather than "never". CartApi removes the code and
 * throws a 409 whose message carries the commercetools state, so these arrive here as
 * ordinary rejections — and they are the likely outcome of the deferred auto-apply, which
 * fires on the *first* add to cart, exactly when a minimum-total predicate is least likely
 * to be met. Deliberately an allow-list: an unknown code and a transport failure are
 * indistinguishable on this path, and holding every unrecognized failure would leave a
 * bogus code retrying silently for the cookie's full 30 days.
 */
const RETRYABLE_REDEEM_STATES = ['DoesNotMatchCart', 'NotActive'];

/**
 * Translates a redeem failure into something a shopper can act on, and says whether the
 * held code is worth keeping. Mirrors the mapping in the manual discount form — the server
 * is the authority on both rules, and a bare "not valid" leaves the shopper with no idea
 * whether to retry.
 */
const describeRedeemError = (error: Error): { message: string; retryable: boolean } => {
  if (error.message.includes('SingleDiscountCodeOnly')) {
    return {
      message: 'Only one discount code can be used per order. Remove the applied code first.',
      retryable: false,
    };
  }

  if (error.message.includes('MaxApplicationReached')) {
    return { message: 'This discount code has reached its redemption limit.', retryable: false };
  }

  if (RETRYABLE_REDEEM_STATES.some((state) => error.message.includes(state))) {
    return {
      message: "That discount code doesn't apply to your cart yet — we'll try it again as your cart changes.",
      retryable: true,
    };
  }

  return { message: "That discount code isn't valid.", retryable: false };
};

/**
 * Tells the shopper what became of a code handed off with a design link (`?design=…&code=…`), in
 * the same words the coupon-link flow uses for the same outcome. The BFF has already redeemed (or
 * declined) it; this only reports, and holds a code that may apply later exactly as a coupon link
 * would, so the existing pending-code retry picks it up as the cart changes.
 */
export const notifyHandoffPromo = (
  promo: BuilderPromoOutcome,
  { couponAppliedEvent, couponFailedEvent }: { couponAppliedEvent: (code: string) => void; couponFailedEvent: (code: string) => void },
) => {
  const { code } = promo;

  if (promo.status === 'applied') {
    couponAppliedEvent(code);
    toast.success(`${code} applied to your cart.`);
    return;
  }

  if (promo.status === 'skipped') {
    toast(
      promo.existingCode
        ? `You already have ${promo.existingCode} applied. Only one code can be used per order — remove it first if you'd rather use ${code}.`
        : 'Only one discount code can be used per order. Remove the applied code first.',
      { duration: 8000 },
    );
    holdPendingPromoCode(code);
    return;
  }

  couponFailedEvent(code);
  const { message, retryable } = describeRedeemError(new Error(promo.state ?? ''));
  Log.error(new Error(`Design handoff code ${code} not applied (${promo.status}${promo.state ? `, ${promo.state}` : ''})`));

  if (retryable) {
    holdPendingPromoCode(code);
    toast(message);
    return;
  }

  toast.error(message);
};

// Module scope, not a ref: `CouponLink` and the globally-mounted `PendingPromoCode` each
// hold their own `usePromoCode()` instance, so a per-hook guard would not span them — and
// spanning them is the whole point. Keyed by code so two different codes never block each
// other.
const inflightCodes = new Set<string>();

const usePromoCode = () => {
  const { data: cart, isEmpty, redeemDiscountCode } = useCart();
  const { couponAppliedEvent, couponFailedEvent } = useGTM();

  const applyPromoCode = useCallback(
    async (code: string, { notify = true }: { notify?: boolean } = {}): Promise<PromoCodeOutcome> => {
      // A second click (or the auto-apply effect firing alongside one) would race the first
      // and can double-count a code's max-application budget.
      if (inflightCodes.has(code)) return 'failed';

      const appliedCodes = cart?.discountCodes ?? [];

      // Compared case-insensitively even though redemption is case-sensitive: this only
      // decides whether to re-send a code the cart already has.
      if (appliedCodes.some((discount) => discount.code?.toLowerCase() === code.toLowerCase())) {
        if (notify) toast(`${code} is already applied to your cart.`);
        clearPendingPromoCode();
        return 'alreadyApplied';
      }

      // Only a code that currently matches the cart counts as occupying the single slot —
      // a dead code (expired, no longer matching) must not block a working one. Same test
      // the manual discount form uses.
      const blockingDiscount = appliedCodes.find((discount) => discount.state === 'MatchesCart');

      if (blockingDiscount) {
        if (notify) {
          toast(
            `You already have ${blockingDiscount.code} applied. Only one code can be used per order — remove it first if you'd rather use ${code}.`,
            { duration: 8000 },
          );
        }
        // Deliberately left pending: if the shopper removes the other code, this one still
        // applies on their next cart change rather than being silently forgotten. Written
        // only when it isn't already stored, so a background retry on every page load
        // doesn't keep renewing the 30-day expiry and outlive the promotion.
        holdPendingPromoCode(code);
        return 'blocked';
      }

      // An empty cart has nothing for the code to attach to, and codes with a predicate
      // (minimum total, specific products) would be rejected outright. Hold it instead —
      // a marketing page is normally read before anything is in the cart.
      // `isEmpty` is also true while the cart request is still in flight, so a shopper who
      // already has items and clicks early would be told the code was merely saved. Guard on
      // `cartId` as well — `PendingPromoCode`'s effect redeems it once real state arrives.
      if (!cart?.cartId || isEmpty || !redeemDiscountCode) {
        holdPendingPromoCode(code);
        if (notify) toast.success(`${code} saved — it'll be applied once you add something to your cart.`);
        return 'deferred';
      }

      inflightCodes.add(code);

      try {
        await redeemDiscountCode(code);
        couponAppliedEvent(code);
        clearPendingPromoCode();
        if (notify) toast.success(`${code} applied to your cart.`);
        return 'applied';
      } catch (error) {
        couponFailedEvent(code);
        const { message, retryable } = describeRedeemError(error as Error);
        Log.error(new Error(`Coupon link redeem failed for ${code}: ${message}`));

        if (retryable) {
          // The cart may qualify later; keep the code so a subsequent cart change retries it.
          holdPendingPromoCode(code);
          if (notify) toast(message);
          return 'notYetEligible';
        }

        // Clearing on a terminal failure keeps a dead code from re-firing this toast on
        // every subsequent cart change.
        clearPendingPromoCode();
        if (notify) toast.error(message);
        return 'failed';
      } finally {
        inflightCodes.delete(code);
      }
    },
    [cart?.cartId, cart?.discountCodes, couponAppliedEvent, couponFailedEvent, isEmpty, redeemDiscountCode],
  );

  return { applyPromoCode, getPendingPromoCode, clearPendingPromoCode };
};

export default usePromoCode;
